import { useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Table, type TableColumn } from '../components/ui/Table';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import type {
  AlarmDto,
  AlarmSeverity,
  ListAlarmHistoryResponse,
  ListPlantsResponse,
} from '../api/types';
import './AlarmsPage.css';

type SeverityFilter = 'All' | AlarmSeverity;

/**
 * Alarm history — pulled, not pushed (concept C.6). Filter controls drive
 * the query; the SignalR store is NOT consulted here because history
 * intentionally includes Cleared rows and rows outside the current live
 * window.
 *
 * Time inputs are `datetime-local` for simplicity; they are converted to
 * ISO strings before being handed to the API.
 */
interface Props { id?: string }

export function AlarmHistoryPage({ id: plantIdFromRoute }: Props = {}) {
  // Default window: last 24 h. `datetime-local` doesn't take a timezone,
  // so we render local time and convert to UTC ISO on send.
  const [from, setFrom] = useState<string>(() => defaultLocalIso(-24));
  const [to, setTo] = useState<string>(() => defaultLocalIso(0));
  const [plantFilter, setPlantFilter] = useState<string>(plantIdFromRoute ?? 'All');
  const lockedToPlant = !!plantIdFromRoute;
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('All');
  // Bumped when the user hits "Aktualisieren" to force a re-fetch.
  const [queryTick, setQueryTick] = useState(0);

  const plantsState = useAsync(
    (signal) => api.get<ListPlantsResponse>('/api/plants', signal),
    [],
  );

  const historyState = useAsync(
    (signal) => {
      const params = new URLSearchParams();
      params.set('from', new Date(from).toISOString());
      params.set('to', new Date(to).toISOString());
      if (plantFilter !== 'All') params.set('plantId', plantFilter);
      return api.get<ListAlarmHistoryResponse>(`/api/alarms/history?${params.toString()}`, signal);
    },
    [queryTick],
  );

  const plantNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (plantsState.status === 'ok') {
      for (const p of plantsState.data.plants) map.set(p.id, p.displayName);
    }
    return map;
  }, [plantsState]);

  const rows = useMemo(() => {
    if (historyState.status !== 'ok') return [];
    const list = severityFilter === 'All'
      ? historyState.data.alarms
      : historyState.data.alarms.filter((a) => a.severity === severityFilter);
    return list.slice().sort((a, b) => (a.triggeredAt < b.triggeredAt ? 1 : -1));
  }, [historyState, severityFilter]);

  const plantOptions = useMemo(() => {
    if (plantsState.status !== 'ok') return [];
    return plantsState.data.plants;
  }, [plantsState]);

  const allColumns: TableColumn<AlarmDto>[] = [
    {
      key: 'severity',
      header: 'Schwere',
      width: '110px',
      render: (a) => (
        <StatusBadge tone={a.severity === 'Alarm' ? 'error' : 'warn'}>
          {a.severity === 'Alarm' ? 'Alarm' : 'Warnung'}
        </StatusBadge>
      ),
    },
    {
      key: 'plant',
      header: 'Anlage',
      render: (a) => plantNameById.get(a.plantId) ?? <code class="mono muted">{a.plantId}</code>,
    },
    {
      key: 'message',
      header: 'Datenpunkt / Meldung',
      render: (a) => <span class="hv-alarms__msg">{a.message}</span>,
    },
    {
      key: 'time',
      header: 'Zeitpunkt',
      width: '160px',
      render: (a) => <span class="mono muted">{formatTimestamp(a.triggeredAt)}</span>,
    },
    {
      key: 'value',
      header: 'Wert / Schwelle',
      width: '160px',
      render: (a) =>
        a.origin === 'Device' || a.triggeredValue == null || a.thresholdValue == null ? (
          <span class="muted">—</span>
        ) : (
          <span class="mono">
            {formatNumber(a.triggeredValue)}{' '}
            <span class="muted">
              {a.direction === 'High' ? '≥' : '≤'} {formatNumber(a.thresholdValue)}
            </span>
          </span>
        ),
    },
    {
      key: 'state',
      header: 'Status',
      width: '130px',
      render: (a) => (
        <StatusBadge tone={a.state === 'Cleared' ? 'ok' : a.state === 'Active' ? 'warn' : 'neutral'}>
          {a.state === 'Cleared' ? 'Ausgeklungen' : a.state === 'Active' ? 'Aktiv' : 'Quittiert'}
        </StatusBadge>
      ),
    },
  ];

  const columns = lockedToPlant
    ? allColumns.filter((c) => c.key !== 'plant')
    : allColumns;
  const plantName = plantIdFromRoute ? (plantNameById.get(plantIdFromRoute) ?? '') : '';

  return (
    <AppShell title={lockedToPlant ? 'Alarm-Verlauf' : 'Alarm-Verlauf (alle Anlagen)'}>
      {lockedToPlant && <PlantSubNav plantId={plantIdFromRoute!} plantName={plantName} />}
      <div class="hv-alarms">
        <div class="hv-alarms__filters">
          <label class="hv-alarms__filter">
            <span>Von</span>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom((e.currentTarget as HTMLInputElement).value)}
            />
          </label>
          <label class="hv-alarms__filter">
            <span>Bis</span>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo((e.currentTarget as HTMLInputElement).value)}
            />
          </label>
          {!lockedToPlant && (
            <label class="hv-alarms__filter">
              <span>Anlage</span>
              <select
                value={plantFilter}
                onChange={(e) => setPlantFilter((e.currentTarget as HTMLSelectElement).value)}
              >
                <option value="All">Alle</option>
                {plantOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.displayName}</option>
                ))}
              </select>
            </label>
          )}
          <label class="hv-alarms__filter">
            <span>Schwere</span>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter((e.currentTarget as HTMLSelectElement).value as SeverityFilter)}
            >
              <option value="All">Alle</option>
              <option value="Alarm">Alarm</option>
              <option value="Warning">Warnung</option>
            </select>
          </label>
          <div class="hv-alarms__spacer" />
          <button
            type="button"
            class="hv-tree-toolbar__btn"
            onClick={() => setQueryTick((n) => n + 1)}
          >
            Aktualisieren
          </button>
          <a class="hv-alarms__history-link"
             href={lockedToPlant ? `/plants/${plantIdFromRoute}/alarms` : '/alarms'}>
            Aktuell →
          </a>
        </div>

        {historyState.status === 'loading' && <p class="muted">Lade Verlauf…</p>}
        {historyState.status === 'error' && <p class="muted">Verlauf konnte nicht geladen werden.</p>}
        {historyState.status === 'ok' && (
          <Table<AlarmDto>
            columns={columns}
            rows={rows}
            rowKey={(a) => a.id}
            emptyLabel="Keine Alarme im gewählten Zeitraum."
          />
        )}
      </div>
    </AppShell>
  );
}

/** Produce a `datetime-local`-friendly value for now + offsetHours. */
function defaultLocalIso(offsetHours: number): string {
  const d = new Date(Date.now() + offsetHours * 3_600_000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const yy = pad(d.getFullYear() % 100);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${yy} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
}
